import { useEffect, useRef } from 'react';
import { CheckCircle2, XCircle, Loader2, Info, AlertTriangle, Bug } from 'lucide-react';
import { fmtDuration } from '../constants.js';

const LEVEL_STYLES = {
  info:  { color: '#94A3B8', icon: Info         },
  warn:  { color: '#F59E0B', icon: AlertTriangle },
  error: { color: '#EF4444', icon: XCircle       },
  debug: { color: '#6366F1', icon: Bug           },
};

function LogLine({ log }) {
  const style = LEVEL_STYLES[log.level] ?? LEVEL_STYLES.info;
  const Icon  = style.icon;

  const ts = log.ts
    ? new Date(log.ts.replace(' ', 'T') + 'Z').toLocaleTimeString(undefined, { hour12: false, fractionalSecondDigits: 3 })
    : '';

  return (
    <div style={{ display: 'flex', gap: 10, padding: '5px 0', alignItems: 'flex-start' }}>
      {/* Time */}
      <span style={{ fontFamily: 'IBM Plex Mono', fontSize: '.7rem', color: 'var(--text-muted)', flexShrink: 0, lineHeight: 1.6 }}>
        {ts}
      </span>

      {/* Level icon */}
      <span style={{ color: style.color, flexShrink: 0, marginTop: 2 }}>
        <Icon size={12} />
      </span>

      {/* Message */}
      <span style={{ fontFamily: 'IBM Plex Mono', fontSize: '.78rem', color: log.level === 'error' ? style.color : 'var(--text-dim)', lineHeight: 1.6, wordBreak: 'break-word' }}>
        {log.message}
      </span>

      {/* Data preview */}
      {log.data && (
        <details style={{ marginLeft: 'auto' }}>
          <summary style={{ fontSize: '.7rem', color: 'var(--text-muted)', cursor: 'pointer', flexShrink: 0 }}>
            data
          </summary>
          <pre style={{
            background: 'var(--surface-3)', padding: '8px 10px', borderRadius: 6,
            fontSize: '.72rem', color: '#e6edf3', margin: '4px 0 0',
            maxWidth: 400, overflow: 'auto',
            fontFamily: 'IBM Plex Mono',
          }}>
            {JSON.stringify(log.data, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function StepGroup({ stepId, stepOutput, logs, steps }) {
  const step     = steps?.find(s => s.id === stepId);
  const stepLogs = logs.filter(l => l.step_id === stepId);
  const out      = stepOutput?.[stepId];

  const statusColor =
    out?.status === 'success' ? 'var(--green)' :
    out?.status === 'error'   ? 'var(--red)'   : 'var(--text-muted)';

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Step header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 12px',
        background: 'var(--surface-2)',
        borderRadius: 6,
        marginBottom: 4,
      }}>
        {out?.status === 'success'
          ? <CheckCircle2 size={13} style={{ color: 'var(--green)' }} />
          : out?.status === 'error'
          ? <XCircle size={13} style={{ color: 'var(--red)' }} />
          : <Loader2 size={13} style={{ color: 'var(--cyan)' }} className="spin" />
        }

        <span style={{ fontFamily: 'IBM Plex Mono', fontSize: '.8rem', fontWeight: 600, color: statusColor }}>
          {step?.name ?? stepId}
        </span>

        <span style={{ marginLeft: 'auto', fontSize: '.72rem', color: 'var(--text-muted)' }}>
          {fmtDuration(out?.duration_ms)}
        </span>
      </div>

      {/* Step logs */}
      <div style={{ paddingLeft: 12, borderLeft: '2px solid var(--border)' }}>
        {stepLogs.map((l, i) => <LogLine key={i} log={l} />)}

        {/* Step output / error */}
        {out?.error && (
          <div style={{ fontFamily: 'IBM Plex Mono', fontSize: '.78rem', color: 'var(--red)', marginTop: 4 }}>
            ✗ {out.error}
          </div>
        )}
        {out?.output && (
          <details style={{ marginTop: 4 }}>
            <summary style={{ fontSize: '.72rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
              Step output
            </summary>
            <pre style={{
              background: '#0d1117', padding: '10px 12px', borderRadius: 6,
              fontSize: '.72rem', color: '#e6edf3', margin: '6px 0 0',
              overflow: 'auto', maxHeight: 200,
              fontFamily: 'IBM Plex Mono',
            }}>
              {JSON.stringify(out.output, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export default function LogsPanel({ run, logs, steps }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  if (!run) return null;

  const workflowLogs = logs.filter(l => !l.step_id);
  const stepIds      = steps?.map(s => s.id) ?? [];

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg)',
    }}>
      {/* Run summary header */}
      <div style={{
        padding: '10px 16px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 12,
        flexShrink: 0,
      }}>
        {run.status === 'success'
          ? <CheckCircle2 size={15} style={{ color: 'var(--green)' }} />
          : run.status === 'error'
          ? <XCircle size={15} style={{ color: 'var(--red)' }} />
          : <Loader2 size={15} className="spin" style={{ color: 'var(--cyan)' }} />
        }
        <span style={{ fontFamily: 'IBM Plex Mono', fontSize: '.82rem', color: 'var(--text)', fontWeight: 600 }}>
          Run {run.id?.slice(0, 8)}
        </span>
        <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
          {fmtDuration(run.duration_ms)}
        </span>
        {run.error && (
          <span style={{ fontSize: '.78rem', color: 'var(--red)', marginLeft: 4 }}>
            {run.error}
          </span>
        )}
      </div>

      {/* Log body */}
      <div className="panel-scroll" style={{ flex: 1, padding: '12px 16px' }}>
        {/* Workflow-level logs (no step_id) */}
        {workflowLogs.map((l, i) => <LogLine key={i} log={l} />)}

        {/* Per-step groups */}
        {stepIds.map(sid => (
          <StepGroup
            key={sid}
            stepId={sid}
            stepOutput={run.steps_output}
            logs={logs}
            steps={steps}
          />
        ))}

        <div ref={bottomRef} />
      </div>
    </div>
  );
}
